import { AppBar, Tab, Tabs } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import SettingsIcon from '@material-ui/icons/Settings';
import { useContext, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import appContext from '../../context/AppContext';
import theme from '../../themes/theme';
import { axiosInstance } from '../../utils/axiosInstance';
import { endpoint, settingsMenuItems } from '../../utils/constants';
import { clearLocalStorage, hasModuleAccess } from '../../utils/helpers';
import FwSubmenu from '../Fw/FwSubmenu';
import GoTopButton from '../GoTopButton/GoTopButton';
import ChangePasswordDialog from '../Submenu/ChangePasswordDialog';
import ProfilSubmenu from '../Submenu/ProfilSubmenu';
import SettingsSubmenu from '../Submenu/SettingsSubmenu';
import SimpleMenu from '../Submenu/Submenu';

const styles = {
    root: {
        flexGrow: 1,
        width: '100%',
    },
    appBar: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 1.5rem',
    },
    tabs: {
        '& .MuiTab-root': {
            minWidth: 'auto',
            padding: '6px 0.75rem',
        },
    },
    profilLabel: {
        color: 'inherit',
        fontSize: '.7rem',
        textTransform: 'uppercase',
        fontWeight: theme.font.weight.normal,
    },
    profilContainer: {
        height: '2.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.4rem',
        color: 'rgba(0, 0, 0, 0.54)',
        '&:hover': {
            cursor: 'pointer',
        },
        '&:focus-visible': {
            outline: `2px solid ${theme.colors.main}`,
            outlineOffset: '2px',
        },
    },
    profilIcon: {
        color: 'inherit',
    },
};

const NavBar = ({ classes: { root, appBar, tabs, profilContainer, profilIcon, profilLabel }, history }) => {
    const { endSession, setSubmenuElement } = useContext(appContext);
    const [isSubmenu, setIsSubmenu] = useState(null);
    const [isFwSubmenu, setIsFwSubmenu] = useState(null);
    const [isSettingsSubmenu, setIsSettingsSubmenu] = useState(null);
    const [isProfilSubmenu, setIsProfilSubmenu] = useState(null);
    const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);

    const canAccessProductionLists = hasModuleAccess('summaries_production');
    const canAccessTestLists = hasModuleAccess('summaries_test');
    const canAccessSummaries = canAccessProductionLists || canAccessTestLists;
    const hasBothLists = canAccessProductionLists && canAccessTestLists;
    const singleSummariesPath = canAccessProductionLists ? '/lists/1' : '/lists/2';

    const setTabHighlight = () => {
        const location = window.location.hash;
        const checkIfLocationIsError = () => {
            const regex = /\berror\b/g;
            return regex.test(location);
        };

        if (checkIfLocationIsError()) {
            return '#/packages';
        }
        if (location.includes('list')) {
            return '#/lists';
        }
        if (location.includes('pipeline')) {
            return '#/pipelines';
        }
        if (location.includes('fw')) {
            return '#/fw';
        }
        if (settingsMenuItems.some(item => location.includes(item.path))) {
            return '#/settings';
        }

        return location;
    };

    const clearData = () => {
        clearLocalStorage();
        endSession();
        history.push('/login');
    };

    const handleLogout = () => {
        axiosInstance
            .post(endpoint.USER_LOGOUT)
            .catch(error => console.error('Error while logging out', error))
            .finally(clearData);
    };

    const handleTabChange = () => {
        setSubmenuElement(null);
        setIsFwSubmenu(null);
        setIsSettingsSubmenu(null);
        setIsProfilSubmenu(null);
    };

    const handleProfilKeyDown = event => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }

        event.preventDefault();
        setIsProfilSubmenu(event.currentTarget);
    };

    return (
        <div className={root}>
            <AppBar position="static" color="default" className={appBar}>
                <Tabs
                    className={tabs}
                    value={setTabHighlight()}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    onChange={handleTabChange}>
                    {hasModuleAccess('start') && (
                        <Tab label="Statystyka" component={RouterLink} to="/" value="#/" />
                    )}
                    {hasModuleAccess('packages') && (
                        <Tab label="Pakiety" component={RouterLink} to="/packages" value="#/packages" />
                    )}
                    {hasModuleAccess('uniques') && (
                        <Tab
                            label="Unikaty"
                            component={RouterLink}
                            to="/serial-numbers"
                            value="#/serial-numbers"
                        />
                    )}
                    {hasModuleAccess('waterworks') && (
                        <Tab label="Wodociągi" component={RouterLink} to="/pipelines" value="#/pipelines" />
                    )}
                    {canAccessSummaries &&
                        (hasBothLists ? (
                            <Tab
                                label="Zestawienia"
                                value={'#/lists'}
                                onClick={e => setIsSubmenu(e.currentTarget)}
                            />
                        ) : (
                            <Tab
                                label="Zestawienia"
                                component={RouterLink}
                                to={singleSummariesPath}
                                value={'#/lists'}
                            />
                        ))}
                    {hasModuleAccess('fw') && (
                        <Tab label="Moduł FW" value={'#/fw'} onClick={e => setIsFwSubmenu(e.currentTarget)} />
                    )}
                    <Tab
                        aria-label="Ustawienia"
                        icon={<SettingsIcon />}
                        value={'#/settings'}
                        onClick={e => setIsSettingsSubmenu(e.currentTarget)}
                    />
                </Tabs>
                <SimpleMenu isSubmenu={isSubmenu} setIsSubmenu={setIsSubmenu} />
                <FwSubmenu isSubmenu={isFwSubmenu} setIsSubmenu={setIsFwSubmenu} />
                <SettingsSubmenu isSubmenu={isSettingsSubmenu} setIsSubmenu={setIsSettingsSubmenu} />
                <ProfilSubmenu
                    isSubmenu={isProfilSubmenu}
                    setIsSubmenu={setIsProfilSubmenu}
                    onChangePassword={() => setIsPasswordDialogOpen(true)}
                    onLogout={handleLogout}
                />
                <ChangePasswordDialog
                    isOpen={isPasswordDialogOpen}
                    onClose={() => setIsPasswordDialogOpen(false)}
                />
                <div
                    role="button"
                    tabIndex={0}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(isProfilSubmenu)}
                    onClick={e => setIsProfilSubmenu(e.currentTarget)}
                    onKeyDown={handleProfilKeyDown}
                    className={profilContainer}>
                    <AccountCircleIcon className={profilIcon} />
                    <Typography className={profilLabel}>Profil</Typography>
                </div>
            </AppBar>
            <GoTopButton />
        </div>
    );
};

export default withStyles(styles)(NavBar);
